import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Icon24Camp, Icon24DaycareCenters, Icon24HomeCareHands } from '@care/react-embrace-icons';
import { Dispatch, SetStateAction } from 'react';
import { theme } from '@care/material-ui-theme';
import useResponsive from '@/components/hooks/useResponsive';
import photoSarah from '@/../public/greaterCare/caregiverReviews/Sarah.jpg';
import photoAllexus from '@/../public/greaterCare/caregiverReviews/Allexus.jpg';
import photoKelly from '@/../public/greaterCare/caregiverReviews/Kelly.jpg';
import { CardType } from '@/constants';
import { AnalyticsEventName, CtaLocation, CtaText, ScreenName } from '@/types/enums';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';
import CaregiverReviewsCarousel from './CaregiverReviewsCarousel';
import CaregiverReviewCard from './CaregiverReviewCard';

export const cards = [
  {
    type: CardType.Caregiver,
    key: 0,
    photo: photoSarah.src,
    name: 'Sarah',
    headline: 'Queen of the playground',
    rating: 4.5,
    experience: 'Sitter with 10 years experience',
    review: {
      name: 'Jennifer M.',
      description: `“Sarah is amazing! She is very interactive with the kids. They ask for her to come back over and over again.”`,
    },
    servicesOffered: [
      'Travel',
      'Meal preparation',
      'Help with pets',
      'Groceries/Errands',
      'Light housecleaning',
      '+11 more',
    ],
  },
  {
    type: CardType.Caregiver,
    key: 1,
    photo: photoAllexus.src,
    name: 'Allexus',
    headline: 'Baby whisperer',
    rating: 5,
    experience: 'Nanny with 7 years experience',
    review: {
      name: 'Amy V.',
      description: `“Allexus was wonderful with our twins. Active, knowledgeable, playful, loving, responsible. We would hire her again in a second.”`,
    },
    servicesOffered: [
      'Carpooling',
      'Organizing/Laundry',
      'Help with pets',
      'Meal preparation',
      'Light housecleaning',
      '+6 more',
    ],
  },
  {
    type: CardType.Caregiver,
    key: 2,
    photo: photoKelly.src,
    name: 'Kelly',
    headline: `Grandma's go-to`,
    rating: 5,
    experience: 'Senior caregiver with 10 years experience',
    review: {
      name: 'Gail M.',
      description: `“Kelly is a very kind and compassionate caregiver. I am very satisfied with the level of care that she provides for my mother!”`,
    },
    servicesOffered: [
      'Respite care',
      'Companion care',
      'Dementia care',
      'Transportation',
      'Light housecleaning',
      '+17 more',
    ],
  },
  {
    type: CardType.Service,
    key: 3,
    headline: 'Great experiences happen outside the home, too.',
    services: [
      { icon: <Icon24Camp />, name: 'Activities and camps' },
      { icon: <Icon24DaycareCenters />, name: 'Daycares' },
      { icon: <Icon24HomeCareHands />, name: 'Senior living communities' },
    ],
  },
];

const GetStartedButton = ({
  setOpenModalOrDrawer,
}: {
  setOpenModalOrDrawer: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isMobile } = useResponsive();

  const handleClick = () => {
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED_VHP,
      data: {
        screen_name: ScreenName.VISITOR_HOMEPAGE,
        enrollment_initiated: true,
        cta_text: CtaText.GET_STARTED,
        cta_location: CtaLocation.VHP_BODY,
        vertical: 'Unknown',
      },
    });

    setOpenModalOrDrawer(true);
  };

  return (
    <Box
      sx={{
        mr: { xs: 3 },
        [theme.breakpoints.up(1360)]: { mr: 0 },
      }}>
      <Button
        onClick={handleClick}
        sx={{ width: { sm: '171px' }, mt: { xs: 4, sm: 0 } }}
        size="large"
        color="primary"
        variant="contained"
        fullWidth={isMobile}>
        Get started
      </Button>
    </Box>
  );
};

const CaregiverReviews = ({
  setOpenModalOrDrawer,
}: {
  setOpenModalOrDrawer: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isMobile } = useResponsive();
  const desktopBreakpoint = 1361;
  const isDesktopView = useMediaQuery(theme.breakpoints.up(desktopBreakpoint));

  return (
    <Box
      sx={{
        justifyItems: { xl: 'center' },
        padding: {
          xs: theme.spacing(6, 0, 6, 3),
          sm: theme.spacing(6, 0, 6, 4),
          md: theme.spacing(8, 0, 6, 10),
        },
        [theme.breakpoints.up(desktopBreakpoint)]: {
          padding: theme.spacing(8, 10, 12),
        },
      }}>
      <Box
        sx={{
          alignItems: { sm: 'center' },
          display: { sm: 'flex' },
          flexDirection: { sm: 'row' },
          justifyContent: { sm: 'space-between' },
          maxWidth: '1279px',
          width: { xl: '100%' },
        }}>
        <Typography
          sx={{
            width: { sm: '445px' },
            [theme.breakpoints.up(1120)]: {
              width: '825px',
            },
            margin: theme.spacing(0, 2, 0, 0),
          }}
          variant={isMobile ? 'display3' : 'display2'}>
          The right care starts with&nbsp;the right people
        </Typography>
        {!isMobile && <GetStartedButton setOpenModalOrDrawer={setOpenModalOrDrawer} />}
      </Box>

      <Box mt={4}>
        {isDesktopView ? (
          <Box display="flex" gap={3} data-testid="caregiver-reviews-desktop">
            {cards.map((card) => (
              <Box key={card.key}>
                <CaregiverReviewCard card={card} />
              </Box>
            ))}
          </Box>
        ) : (
          <CaregiverReviewsCarousel cards={cards} />
        )}
      </Box>

      {isMobile && <GetStartedButton setOpenModalOrDrawer={setOpenModalOrDrawer} />}
    </Box>
  );
};

export default CaregiverReviews;
